import { useMemo } from 'react';
import type { Booking } from '@/types';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import { isUnallocatedBooking } from '@/utils/unallocated-booking';

export type UrgencyLevel = 'critical' | 'soon' | 'upcoming';

export interface UnallocatedBooking extends Booking {
  urgency: UrgencyLevel;
  daysUntilStart: number;
}

/**
 * Returns all bookings nobody is assigned to, enriched with urgency metadata
 * and sorted soonest-first.
 */
export function useUnallocatedBookings(bookings: Booking[]): UnallocatedBooking[] {
  return useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return bookings
      .filter(isUnallocatedBooking)
      .map(b => {
        const start = parseISO(b.startTime);
        const daysUntilStart = differenceInCalendarDays(start, today);

        let urgency: UrgencyLevel;
        if (daysUntilStart <= 3) {
          urgency = 'critical';
        } else if (daysUntilStart <= 7) {
          urgency = 'soon';
        } else {
          urgency = 'upcoming';
        }

        return { ...b, urgency, daysUntilStart };
      })
      .sort((a, b) => a.daysUntilStart - b.daysUntilStart);
  }, [bookings]);
}
